import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, MapPin, ArrowRight, PhoneCall } from 'lucide-react';
import { suburbs } from '../../data/suburbs';
import { business } from '../../mock';

// A postcode / suburb checker for the Service Areas section.
// Type a suburb name or postcode and instantly see whether we service it.
const AREAS = [
  ...suburbs.map((s) => ({
    name: s.name,
    slug: s.slug,
    postcode: s.postcode,
    path: `/service-areas/${s.slug}`,
  })),
];

export default function SuburbChecker({ variant = 'dark', onQuoteClick }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const dark = variant === 'dark';

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (q.length < 2) return [];
    return AREAS.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.slug.replace(/-/g, ' ').includes(q) ||
        a.postcode.startsWith(q),
    ).slice(0, 6);
  }, [q]);

  const showNoMatch = q.length >= 2 && matches.length === 0;

  const go = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (matches.length > 0) go(matches[0].path);
  };

  return (
    <div
      className={`rounded-xl p-5 md:p-6 ${
        dark ? 'bg-white/[0.08] border border-white/20' : 'bg-white border border-[#E2DAC5] shadow-sm'
      }`}
      data-testid="suburb-checker"
    >
      <p className={`text-sm font-bold mb-3 ${dark ? 'text-white' : 'text-[#0A2E18]'}`}>
        Not sure if we cover you? Check your suburb or postcode.
      </p>

      <form onSubmit={onSubmit} className="relative">
        <Search
          className={`w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 ${
            dark ? 'text-[#0A2E18]/50' : 'text-[#4A5447]'
          }`}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. Lucas or 3350"
          data-testid="suburb-checker-input"
          aria-label="Enter your suburb or postcode"
          className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white text-[#0A2E18] placeholder-[#525B4E] font-medium border-2 border-transparent focus:outline-none focus:border-[#D98A00]"
        />
      </form>

      {matches.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="suburb-checker-results">
          {matches.map((m) => (
            <li key={m.slug}>
              <button
                type="button"
                onClick={() => go(m.path)}
                data-testid={`suburb-checker-result-${m.slug}`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#1B6E37]/10 hover:bg-[#1B6E37]/20 border border-[#1B6E37]/30 text-left transition-colors group"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <CheckCircle2 className="w-5 h-5 text-[#1B6E37] shrink-0" />
                  <span className="min-w-0">
                    <span className={`block font-bold truncate ${dark ? 'text-white' : 'text-[#0A2E18]'}`}>
                      Yes, we service {m.name}
                    </span>
                    <span className={`block text-xs ${dark ? 'text-white/70' : 'text-[#3D463C]'}`}>
                      Postcode {m.postcode}
                    </span>
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-[#1B6E37] shrink-0 group-hover:gap-2 transition-all">
                  View <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showNoMatch && (
        <div
          className="mt-3 px-4 py-4 rounded-lg bg-[#D98A00]/15 border border-[#D98A00]/40"
          data-testid="suburb-checker-no-match"
        >
          <div className="flex items-start gap-2.5">
            <MapPin className="w-5 h-5 text-[#B86F00] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className={`font-bold mb-1 ${dark ? 'text-white' : 'text-[#0A2E18]'}`}>
                We might still cover you.
              </p>
              <p className={`text-sm mb-3 ${dark ? 'text-white/75' : 'text-[#212B20]'}`}>
                We service a wide area beyond our listed suburbs. Get in touch and we will confirm
                straight away.
              </p>
              <button
                type="button"
                onClick={onQuoteClick}
                data-testid="suburb-checker-contact-btn"
                className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm"
              >
                <PhoneCall className="w-4 h-4" /> Contact us to check
              </button>
            </div>
          </div>
        </div>
      )}

      <p className={`mt-3 text-sm ${dark ? 'text-white/70' : 'text-[#3D463C]'}`} data-testid="suburb-checker-call-line">
        Can&rsquo;t find your suburb?{' '}
        <a
          href={`tel:${business.phone}`}
          data-testid="suburb-checker-call-link"
          className={`font-bold inline-flex items-center gap-1 underline underline-offset-2 ${
            dark ? 'text-[#D98A00] hover:text-white' : 'text-[#1B6E37] hover:text-[#0A2E18]'
          }`}
        >
          <PhoneCall className="w-3.5 h-3.5" /> Call us on {business.phone}
        </a>
      </p>
    </div>
  );
}
